import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, RotateCcw } from 'lucide-react';
import { GROCERY_CATEGORIES, type GroceryCategory, type Store } from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@recipes/ui';
import { toast } from '@/components/feedback/toaster';
import { groceryCategoryLabel } from '@/features/shared/labels';
import { toastError } from '@/lib/errors';
import { updateStoreLocal } from './catalog-queue';
import { departmentOrderOf } from './department-order';

/**
 * "In which order do you walk through this store?" – every department with an up and a down
 * button (no dragging: it has to work with one thumb and with a keyboard). Saved on the device
 * first, so it also works between the shelves without a connection.
 */
export function DepartmentOrderDialog({
  store,
  stores,
  open,
  onOpenChange,
  inUse,
}: {
  store: Store;
  stores: Store[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Departments that have items on the list right now: shown first-class, the rest muted. */
  inUse?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const [order, setOrder] = useState<GroceryCategory[]>(() => departmentOrderOf(store, stores));
  const [saving, setSaving] = useState(false);
  const [moved, setMoved] = useState<string | null>(null);
  useEffect(() => {
    if (open) setOrder(departmentOrderOf(store, stores));
    // Only when the dialog opens: a sync that arrives meanwhile must not undo what was moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, store.id]);

  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    const [entry] = next.splice(index, 1);
    next.splice(target, 0, entry as GroceryCategory);
    setOrder(next);
    setMoved(
      t('catalog.departments.moved', {
        name: groceryCategoryLabel(t, entry as string),
        position: target + 1,
        count: next.length,
      }),
    );
  };

  const save = async (next: GroceryCategory[]) => {
    setSaving(true);
    try {
      await updateStoreLocal(store, { departmentOrder: next });
      toast.success(t('catalog.departments.saved', { store: store.name }));
      onOpenChange(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const isDefault = order.every((c, i) => c === GROCERY_CATEGORIES[i]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="department-order-dialog">
        <DialogHeader>
          <DialogTitle>{t('catalog.departments.title', { store: store.name })}</DialogTitle>
          <DialogDescription>{t('catalog.departments.description')}</DialogDescription>
        </DialogHeader>
        <ol className="max-h-[55vh] divide-y overflow-y-auto rounded-lg border">
          {order.map((category, index) => {
            const name = groceryCategoryLabel(t, category);
            return (
              <li
                key={category}
                className="flex min-h-[48px] items-center gap-2 px-3"
                data-testid="department-row"
                data-category={category}
              >
                <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {index + 1}
                </span>
                <span
                  className={
                    inUse && !inUse.has(category)
                      ? 'flex-1 text-sm text-muted-foreground'
                      : 'flex-1 text-sm font-medium'
                  }
                >
                  {name}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-11"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={t('catalog.departments.up', { name })}
                >
                  <ArrowUp aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-11"
                  disabled={index === order.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={t('catalog.departments.down', { name })}
                >
                  <ArrowDown aria-hidden />
                </Button>
              </li>
            );
          })}
        </ol>
        <p className="sr-only" role="status" aria-live="polite">
          {moved}
        </p>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={saving || (isDefault && (store.departmentOrder ?? []).length === 0)}
            onClick={() => void save([])}
            data-testid="department-order-reset"
          >
            <RotateCcw aria-hidden /> {t('catalog.departments.reset')}
          </Button>
          <Button
            type="button"
            loading={saving}
            onClick={() => void save(order)}
            data-testid="department-order-save"
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
